/**
 * IT PDP — the size chart's toggles.
 *
 * The catalogue files each product's chart as two finished tables, one in CM
 * and one in inches, with sizes down the left. The design runs measurements
 * down the left and sizes across the top, so this transposes them — which is
 * also what makes the row labels read as labels rather than a row of numbers.
 *
 * Garment figures are the catalogue's own. Body figures are derived by
 * subtracting the ease the merchant records on the block; a measurement with
 * no ease on file is shown unchanged rather than guessed at.
 *
 * The catalogue's markup stays in the page, hidden, so a visitor whose
 * JavaScript never arrives still sees the real chart instead of nothing.
 */
class ItSizeChart extends HTMLElement {
  #type = 'garment';
  #unit = 'in';
  #tables = {};
  #fit = '';
  #ease = new Map();

  connectedCallback() {
    const source = this.querySelector('[data-source]');
    const out = this.querySelector('[data-tables]');
    if (!source || !out) return;

    this.#ease = this.#readEase();
    this.#fit = source.querySelector('h1, h2, h3, h4')?.textContent.trim() ?? '';

    // The catalogue ids these `table-cm` and `table-inch`. Fall back to
    // document order so a chart filed without ids still renders.
    const all = [...source.querySelectorAll('table')];
    this.#tables.cm = source.querySelector('#table-cm') ?? all[0] ?? null;
    this.#tables.in = source.querySelector('#table-inch') ?? all[1] ?? all[0] ?? null;

    if (!this.#tables.in && !this.#tables.cm) return;

    source.hidden = true;
    out.hidden = false;

    this.addEventListener('click', this.#onClick);
    this.#draw();
  }

  /** @returns {Map<string, number>} Ease in inches, keyed by lowercased measurement. */
  #readEase() {
    const map = new Map();
    for (const line of (this.dataset.bodyEase ?? '').split('\n')) {
      const [name, value] = line.split(':');
      const inches = Number.parseFloat(value);
      if (name?.trim() && Number.isFinite(inches)) map.set(name.trim().toLowerCase(), inches);
    }
    return map;
  }

  #onClick = (event) => {
    const button = event.target.closest('.it-sc__seg button');
    if (!button || !this.contains(button)) return;

    const seg = button.closest('[data-seg]');
    if (seg?.dataset.seg === 'type') this.#type = button.dataset.value;
    if (seg?.dataset.seg === 'unit') this.#unit = button.dataset.value;

    for (const other of seg.querySelectorAll('button')) {
      other.classList.toggle('is-on', other === button);
    }

    this.#draw();
  };

  /**
   * Read one source table into { sizes, rows } with measurements as rows.
   * @param {HTMLTableElement} table - A catalogue table, sizes down the left
   * @returns {{sizes: string[], rows: {name: string, values: string[]}[]} | null}
   */
  #parse(table) {
    if (!table) return null;

    const headerCells = [...(table.querySelector('thead tr')?.children ?? [])].map((c) =>
      c.textContent.trim()
    );
    const bodyRows = [...(table.querySelectorAll('tbody tr') ?? [])];
    if (headerCells.length < 2 || bodyRows.length === 0) return null;

    // First header cell labels the size column; the rest are measurements.
    const measurements = headerCells.slice(1);
    const sizes = bodyRows.map((tr) => tr.children[0]?.textContent.trim() ?? '');

    const rows = measurements.map((name, column) => ({
      name,
      values: bodyRows.map((tr) => tr.children[column + 1]?.textContent.trim() ?? ''),
    }));

    return { sizes, rows };
  }

  /**
   * @param {string} raw - A figure as the catalogue filed it
   * @param {string} name - The measurement's name
   * @returns {string} The figure to print, ease applied when showing body
   */
  #value(raw, name) {
    if (this.#type !== 'body') return raw;

    const ease = this.#ease.get(name.trim().toLowerCase());
    const figure = Number.parseFloat(raw);
    if (ease === undefined || !Number.isFinite(figure)) return raw;

    // Ease is recorded in inches; a CM table needs it converted.
    const adjusted = figure - (this.#unit === 'cm' ? ease * 2.54 : ease);
    return adjusted.toFixed(1);
  }

  #draw() {
    const out = this.querySelector('[data-tables]');
    const data = this.#parse(this.#tables[this.#unit]);
    if (!out || !data) return;

    const head = `<tr><th scope="col">Size</th>${data.sizes
      .map((size) => `<th scope="col">${size}</th>`)
      .join('')}</tr>`;

    const body = data.rows
      .map(
        (row) =>
          `<tr><th scope="row">${row.name}</th>${row.values
            .map((value) => `<td>${this.#value(value, row.name)}</td>`)
            .join('')}</tr>`
      )
      .join('');

    out.innerHTML =
      `<div class="it-sc__tbl">` +
      `<div class="it-fly__scroll"><table class="it-sz"><thead>${head}</thead><tbody>${body}</tbody></table></div></div>`;

    this.#syncMeasureList(data.rows.map((row) => row.name.trim().toLowerCase()));
  }

  /**
   * Only describe the measurements this product's chart actually lists —
   * a trouser has no collar, and a shirt has no inseam.
   * @param {string[]} names - Measurement names present in the table
   */
  #syncMeasureList(names) {
    const list = this.closest('.it-fly__pane')?.querySelector('[data-measure]');
    if (!list) return;

    let shown = 0;
    for (const item of list.children) {
      const on = names.includes(item.dataset.name ?? '');
      item.hidden = !on;
      if (on) shown += 1;
    }

    // Nothing matched — the chart uses names the list does not cover, and an
    // empty heading with no items under it reads as a fault. Show them all.
    if (shown === 0) {
      for (const item of list.children) item.hidden = false;
    }
  }
}

if (!customElements.get('it-size-chart')) {
  customElements.define('it-size-chart', ItSizeChart);
}
